import { useEffect, useState } from "react";
import { isHoloCard, ygoRarityLabel } from "@/lib/rarityTier";
import { condDesc } from "@/lib/conditions";
import { BusyLabel, HitSectionLoading, useLoadingLevel } from "@/components/loading";
import { Link, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, publicImageUrl, cardImageUrl, cardName } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { useDialog } from "@/lib/dialog";
import { useLightbox } from "@/lib/lightbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Flag, ShoppingCart, MapPin, PackageX, Award, Heart } from "lucide-react";
import { toast } from "sonner";
import PriceHistoryChart from "@/components/PriceHistoryChart";
import { HitCardTilt } from "@/components/catalog/HitCardTilt";
import { Seo } from "@/components/Seo";
import { cardPath, parseCardIdFromSlug } from "@/lib/cardUrl";
import { HitAvatar } from "@/components/avatar/HitAvatar";
import { RankName, frameOf } from "@/components/avatar/RankPanel";

const LANG_LABEL = { es: "Español", en: "Inglés", ja: "Japonés", otro: "Otro" };
const VARIANT_LABEL = { normal: "Normal", reverse_holo: "Retroholográfica", price_pack: "Price Pack" };
const variantLabelFor = (v, card) => (v === "normal" ? (isHoloCard(card) ? "Holo" : "Normal") : (VARIANT_LABEL[v] || v));
const VARIANT_STYLE = {
  normal: "bg-neutral-500/20 text-neutral-200 border-neutral-500/30",
  reverse_holo: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  price_pack: "bg-black/85 text-amber-300 border-amber-400/50",
};

const PAGE_SIZE = 12; // publications loaded per "Ver más" click

function GradedAvailable({ list, cardId, name, language, variant }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="graded-available">
      {list.map((g) => (
        <Link key={g.key} data-testid={`graded-available-${g.key}`}
              to={cardPath({ cardId, name, language, variant, graded: g.key })}
              className="text-xs border border-emerald-500/40 text-emerald-300 rounded px-2.5 py-1 hover:bg-emerald-500/10">
          Disponible graduada: {g.house} {g.grade} ({g.count}) · ${Number(g.buyer_pays).toFixed(2)} →
        </Link>
      ))}
    </div>
  );
}

// Yu-Gi-Oh: el set real es el de la impresión publicada, no el primer set del catálogo.
const ygoSetLabel = (card, pubs) => {
  if (card.game_slug !== "yugioh") return card.set_name;
  const sets = [...new Set((pubs || []).filter((p) => p.yugioh_set_code).map((p) => p.yugioh_set_name || p.yugioh_set_code))];
  if (sets.length === 1) return sets[0];
  return sets.length > 1 ? "Varias impresiones" : card.set_name;
};

export default function CardDetail() {
  const params = useParams();
  // Router acepta dos formas de URL:
  //   - /card/:cardId/:language[/:variant]        (legacy, compat)
  //   - /carta/:slugId/:language[/:variant]        (SEO — slugId = "{slug}--{cardId}")
  const cardId = params.cardId || parseCardIdFromSlug(params.slugId);
  const { language, variant } = params;
  const [searchParams] = useSearchParams();
  const gradedKey = searchParams.get("graded") || "raw";
  const activeVariant = variant || "normal";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pageLevel = useLoadingLevel(loading);
  const [addingId, setAddingId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { user } = useAuth();
  const { add: addToCart } = useCart();
  const { prompt } = useDialog();
  const { open: openLightbox } = useLightbox();
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get(`/catalog/variant/${cardId}/${language}`, { params: { variant: activeVariant, graded: gradedKey } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [cardId, language, activeVariant, gradedKey]);

  // Wishlist state — heart toggle next to the card title. Cheap check runs once
  // per (card, lang, variant, graded) combo and only when a buyer is signed in.
  const [wl, setWl] = useState({ in: false, id: null, busy: false });
  useEffect(() => {
    if (!user) { setWl({ in: false, id: null, busy: false }); return; }
    api.get("/wishlist/check", { params: { card_id: cardId, language, variant: activeVariant, graded_key: gradedKey } })
      .then(r => setWl(s => ({ ...s, in: r.data.in_wishlist, id: r.data.id })))
      .catch(() => {});
  }, [user, cardId, language, activeVariant, gradedKey]);

  const toggleWishlist = async () => {
    if (!user) { nav("/login"); return; }
    setWl(s => ({ ...s, busy: true }));
    try {
      if (wl.in && wl.id) {
        await api.delete(`/wishlist/${wl.id}`);
        setWl({ in: false, id: null, busy: false });
        toast.success("Quitado de tu wishlist");
      } else {
        const r = await api.post("/wishlist", { card_id: cardId, language, variant: activeVariant, graded_key: gradedKey });
        setWl({ in: true, id: r.data.id, busy: false });
        toast.success("Guardado en tu wishlist");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
      setWl(s => ({ ...s, busy: false }));
    }
  };

  const buy = async (pubId) => {
    setAddingId(pubId);
    try { await buyInner(pubId); } finally { setAddingId(null); }
  };

  const buyInner = async (pubId) => {
    if (!user) { nav("/login"); return; }
    if (user.verification_status !== "aprobado") { toast.error("Debes verificar tu identidad primero"); nav("/verify"); return; }
    try {
      const r = await addToCart(pubId, 1);
      const remaining = r?.remaining;
      if (remaining === 0) {
        toast.success("Agregado ✓ · Ya tienes el stock máximo de esta publicación", { duration: 3000 });
      } else {
        toast.success("Agregado al carrito ✓", { duration: 2500 });
      }
    } catch (err) {
      // El backend devuelve `detail` con el motivo real (stock máximo, no puedes comprarte, etc.)
      toast.error(err.response?.data?.detail || "No se pudo agregar");
    }
  };

  const report = async (target_type, target_id) => {
    if (!user) { nav("/login"); return; }
    const reason = await prompt({
      title: "Reportar publicación",
      label: "Motivo",
      placeholder: "¿Qué está mal con esta publicación?",
      multiline: true,
      required: true,
    });
    if (!reason) return;
    await api.post("/reports", { target_type, target_id, reason });
    toast.success("Reporte enviado");
  };

  if (loading || pageLevel !== "none") return <div className="max-w-7xl mx-auto px-5 py-8 min-h-[70vh]"><HitSectionLoading level={pageLevel} loading={loading} section="card" /></div>;
  if (!data) return <div className="max-w-7xl mx-auto p-8 text-neutral-500">Carta no encontrada</div>;

  const { card, publications, reference, market, sales_count, in_stock, last_snapshot } = data;
  const gradedAvail = (!data.graded && data.graded_available) || [];
  const fromListing = data.from;
  const marketDisplay = market || (last_snapshot ? {
    avg: last_snapshot.avg,
    median: last_snapshot.median,
    min: last_snapshot.min,
    max: last_snapshot.max,
    listings: last_snapshot.listings,
    from_history: true,
    last_date: last_snapshot.date,
  } : null);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <Seo
        title={`${cardName(card, language)} — Precio y venta en Ecuador`}
        description={`Compra ${cardName(card, language)} (${card.set_name || ""}) en Ecuador. ${in_stock ? `Desde $${(publications[0]?.buyer_pays ?? publications[0]?.price ?? 0).toFixed(2)}.` : "Historial de precios de mercado."} Escrow y envíos a todo el país.`}
        keywords={`${cardName(card, language)}, precio ${cardName(card, language)} Ecuador, comprar ${cardName(card, language)}, ${card.set_name || ""}`}
        image={cardImageUrl(card.image_url)}
        type="product"
        url={cardPath({ cardId, name: cardName(card, language), language, variant: activeVariant })}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": cardName(card, language),
          "description": `${cardName(card, language)} — ${card.set_name || ""}`,
          "image": card.image_url,
          "offers": in_stock && publications[0] ? {
            "@type": "Offer",
            "priceCurrency": "USD",
            "price": (publications[0].buyer_pays ?? publications[0].price ?? 0).toFixed(2),
            "availability": "https://schema.org/InStock",
            "url": typeof window !== "undefined" ? window.location.href : "",
          } : {
            "@type": "Offer",
            "availability": "https://schema.org/OutOfStock",
          },
        }}
      />
      <div className="sx flex items-center justify-between mb-4 lg:mb-6">
        <Link
          to={location.state?.from || "/catalog"}
          state={{ restoreCatalog: true }}
          data-testid="card-back-link"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/10 bg-[#101010] text-white/80 hover:text-white text-sm font-semibold transition-colors"
        >
          <ArrowLeft size={16} /> Volver{location.state?.fromLabel ? ` a ${location.state.fromLabel}` : " al catálogo"}
        </Link>
        <button
          type="button"
          onClick={toggleWishlist}
          disabled={wl.busy}
          data-testid="wishlist-heart"
          aria-label={wl.in ? "Quitar de wishlist" : "Agregar a wishlist"}
          title={wl.in ? "Quitar de wishlist" : "Agregar a wishlist"}
          className={`w-10 h-10 rounded-full border grid place-items-center transition-colors ${wl.in ? "bg-[rgba(255,68,56,.15)] border-[#FF4438] text-[#FF4438]" : "bg-[#101010] border-white/10 text-white/60 hover:text-[#FF4438]"}`}
        >
          <Heart size={18} fill={wl.in ? "currentColor" : "none"} />
        </button>
      </div>

      <div className={`sx grid grid-cols-1 lg:grid-cols-[440px_1fr] gap-6 lg:gap-10 lg:pb-0 ${fromListing ? "pb-[calc(96px+env(safe-area-inset-bottom,0px))]" : ""}`} data-testid="card-detail-grid">
        <div>
          <div className="lg:sticky lg:top-[89px]">
            <div className="relative py-6 lg:py-8">
              <span className="sx-mono absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 origin-left translate-x-3 text-[9px] uppercase tracking-[.2em] text-white/40 whitespace-nowrap" data-testid="card-number-vertical">
                Núm. {card.number || "—"} · {card.id}
              </span>
              <HitCardTilt>
              {(() => {
                const cardImg = card.image_url;
                const idPhoto = card.identification_photos?.[0];
                const pubPhoto = publications[0]?.photos?.[0];
                const src = cardImg
                  ? cardImageUrl(cardImg)
                  : (idPhoto ? publicImageUrl(idPhoto) : (pubPhoto ? publicImageUrl(pubPhoto) : null));
                if (!src) return <div className="absolute inset-0 grid place-items-center text-white/40 text-sm">Sin imagen</div>;
                return <img src={src} alt={cardName(card, language)} className="w-full h-full object-cover" draggable={false} onError={(e) => { e.currentTarget.style.display = "none"; }} />;
              })()}
              </HitCardTilt>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="min-w-0 space-y-6">
            <div className="space-y-2 lg:pt-6" data-testid="card-title-block">
              <div className="flex flex-wrap gap-1.5">
                <span className="sx-mono text-[9px] uppercase px-2 py-1 rounded-md bg-[#FF4438] text-[#080808]">{LANG_LABEL[language] || language}</span>
                {(card.is_sealed || activeVariant === "sealed" || String(card.product_type || "").startsWith("sealed_")) ? (
                  <span className="sx-mono text-[9px] uppercase px-2 py-1 rounded-md bg-white text-[#080808]" data-testid="card-sealed-badge">Producto sellado</span>
                ) : card.rarity && !["common", "none", ""].includes(String(card.rarity).toLowerCase()) ? (
                  <span className="sx-mono text-[9px] uppercase px-2 py-1 rounded-md border border-[#fbbf24] text-[#fbbf24]" data-testid="card-rarity-badge">{card.rarity}</span>
                ) : null}
                {!(card.is_sealed || activeVariant === "sealed" || String(card.product_type || "").startsWith("sealed_")) && (card.game_slug || "pokemon") === "pokemon" && (
                  <span className="sx-mono text-[9px] uppercase px-2 py-1 rounded-md border border-[#fbbf24] text-[#fbbf24]" data-testid="card-variant-badge">{variantLabelFor(activeVariant, card)}</span>
                )}
                {data.graded && (
                  <Badge className="bg-[#fbbf24] text-[#080808] font-bold" data-testid="card-graded-badge">
                    <Award size={12} className="mr-1" /> {data.graded.house} {data.graded.grade}
                  </Badge>
                )}
                {!in_stock && (
                  <Badge className="bg-white/5 text-white/70 border-white/15 border" data-testid="no-stock-badge">
                    <PackageX size={12} className="mr-1" /> Sin stock disponible
                  </Badge>
                )}
              </div>
              <h1 className="text-[38px] lg:text-[64px] xl:text-[72px] font-extrabold tracking-[-.05em] leading-[.95]" data-testid="card-title">
                {(() => {
                  const n = cardName(card, language);
                  const m = n.match(/^(.*)\s(ex|EX|V|VMAX|VSTAR|GX)$/);
                  return m ? <>{m[1]} <span className="italic font-normal text-white/60">{m[2]}</span></> : n;
                })()}
              </h1>
              <div className="text-white/50 text-sm lg:text-base" data-testid="card-set-name">{ygoSetLabel(card, publications)}</div>
              {(card.brand || card.size) && <div className="text-white/60 text-sm" data-testid="card-brand-size">{[card.brand && `Marca: ${card.brand}`, card.size && `Medida: ${card.size}`].filter(Boolean).join(" · ")}</div>}
            </div>
          {/* Bundle ("grupo de cartas"): mostramos "¿Qué incluye?" en vez de
              precio de mercado / precio HIT / historial. Cada bundle es único
              y no comparable — el precio del vendedor manda. Feb 2026. */}
          {card.is_bundle ? (
            <>
              <div className="bg-[#111] border border-yellow-500/40 rounded-md p-5" data-testid="bundle-contents-block">
                <div className="flex items-center gap-2 text-yellow-300 text-xs uppercase tracking-wider mb-3">
                  <span className="w-2 h-2 rounded-full bg-yellow-400" />
                  {card.bundle_subtype === "bulk" ? "Bulk · Lote de cartas sueltas"
                    : card.bundle_subtype === "deck" ? "Deck · Mazo armado"
                    : card.bundle_subtype === "base" ? "Base · Estructura base de deck"
                    : "Grupo de cartas"}
                </div>
                <div className="font-display text-sm text-neutral-400 uppercase tracking-wider mb-2">¿Qué incluye?</div>
                <pre className="whitespace-pre-wrap font-mono text-sm text-neutral-200 bg-[#0A0A0A] border border-[#262626] rounded p-3 leading-relaxed break-words" data-testid="bundle-contents">
{card.bundle_contents || "(el vendedor no describió el contenido)"}
                </pre>
                {card.custom_notes && (
                  <div className="mt-3 text-xs text-neutral-400">
                    <b className="text-neutral-300">Notas del vendedor:</b> {card.custom_notes}
                  </div>
                )}
                <div className="mt-4 text-[11px] text-yellow-100/70 leading-relaxed">
                  💡 Los grupos son productos únicos (contenido escrito por el vendedor). No tienen precio de mercado ni historial — cada grupo es distinto, así que el precio del vendedor es la única referencia. Las <b>fotos reales</b> son la fuente de verdad: revísalas antes de comprar.
                </div>
              </div>
            </>
          ) : (
          <>
          {/* PRECIO DE VENTA ("Desde $X") + PRECIO DE MERCADO — estilo TCGplayer */}
          <div className="grid grid-cols-2 gap-2.5 lg:gap-3">
            {/* Desde $X — precio que paga el comprador (incluye comisión + procesamiento) */}
            <div className="bg-[rgba(255,68,56,.08)] border border-[rgba(255,68,56,.45)] rounded-[18px] p-4 lg:p-5 min-w-0" data-testid="card-price-from">
              <div className="sx-mono text-[9px] uppercase tracking-[.14em] text-[#ff8a80] mb-1.5">
                Precio HIT
              </div>
              {fromListing ? (
                <>
                  <div className="text-[30px] lg:text-[40px] font-extrabold tracking-[-.04em] leading-none text-[#FF4438]">${fromListing.buyer_pays.toFixed(2)}</div>
                  <div className="text-[11px] text-white/50 mt-1.5">Precio más bajo</div>
                </>
              ) : gradedAvail.length > 0 ? (
                <>
                  <div className="font-display text-xl text-neutral-400">Sin stock sin graduar</div>
                  <GradedAvailable list={gradedAvail} cardId={cardId} name={cardName(card, language)} language={language} variant={activeVariant} />
                </>
              ) : (
                <>
                  <div className="font-display text-2xl text-neutral-500">— sin stock —</div>
                  <div className="text-[11px] text-amber-400 mt-1">Nadie tiene esta carta publicada ahora mismo.</div>
                </>
              )}
            </div>
            {/* Precio de mercado — referencia limpia (media de precios base de vendedores) */}
            <div className="bg-[#101010] border border-white/10 rounded-[18px] p-4 lg:p-5 min-w-0" data-testid="card-price-market">
              <div className="sx-mono text-[9px] uppercase tracking-[.14em] text-white/50 mb-1.5">
                Mercado
              </div>
              {marketDisplay ? (
                <>
                  <div className="text-[30px] lg:text-[40px] font-extrabold tracking-[-.04em] leading-none text-white">${(marketDisplay.avg || 0).toFixed(2)}</div>
                  <div className="text-[11px] text-white/50 mt-1.5">
                    {marketDisplay.from_history
                      ? `Último registro: ${marketDisplay.last_date}`
                      : `Promedio · ${marketDisplay.listings} publicaci${marketDisplay.listings === 1 ? "ón" : "ones"} (activas + vendidas + retiradas)`}
                  </div>
                </>
              ) : (
                <div className="text-neutral-400 text-sm">
                  Aún no hay suficientes publicaciones para calcular el precio de mercado (mín. 2). Listados actuales: <b>{publications.length}</b>.
                </div>
              )}
            </div>
          </div>

          {/* Gráfica de historial de precios */}
          <PriceHistoryChart cardId={cardId} language={language} variant={activeVariant} />

          {reference && (
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4 text-xs text-neutral-400" data-testid="card-price-sales-ref">
              Historial de ventas cerradas: <span className="text-white font-medium">${reference.avg.toFixed(2)}</span> promedio ·
              <span className="text-white font-medium ml-1">${reference.median.toFixed(2)}</span> mediana ·
              <span className="text-white font-medium ml-1">{reference.sales_count}</span> ventas.
            </div>
          )}
          {!reference && sales_count > 0 && (
            <div className="text-xs text-neutral-500" data-testid="card-price-sales-empty">
              Ventas cerradas: <b>{sales_count}</b> (aún no hay suficientes para promedio de ventas).
            </div>
          )}
          </>
          )}

          {/* Publications list — sorted cheapest-first (server ORDER BY price ASC) + "Ver más" pagination */}
          <div id="card-publications" className="scroll-mt-24">
            <div className="flex items-end justify-between gap-3 mb-3">
              <h2 className="text-[26px] font-extrabold tracking-[-.04em]">{publications.length} <span className="italic font-normal text-white/60">publicaci{publications.length === 1 ? "ón" : "ones"}</span></h2>
              {publications.length > 0 && (
                <div className="sx-mono text-[9px] uppercase tracking-[.14em] text-white/45 pb-1.5">Más barata → más cara</div>
              )}
            </div>
            {publications.length === 0 && gradedAvail.length > 0 && (
              <div className="border border-dashed border-[#262626] rounded-md p-6 text-neutral-300 space-y-2" data-testid="graded-only-block">
                <div className="text-sm">No hay publicaciones <b>sin graduar</b> de esta carta, pero sí está disponible <b className="text-emerald-400">graduada</b>:</div>
                <GradedAvailable list={gradedAvail} cardId={cardId} name={cardName(card, language)} language={language} variant={activeVariant} />
              </div>
            )}
            {publications.length === 0 && gradedAvail.length === 0 && (
              <div className="border border-dashed border-[#262626] rounded-md p-8 text-center text-neutral-400 space-y-2" data-testid="no-stock-block">
                <PackageX size={32} className="mx-auto text-amber-400" />
                <div className="text-sm">Esta carta está en el catálogo pero <b className="text-amber-300">nadie tiene stock activo</b> ahora mismo.</div>
                <div className="text-xs text-neutral-500">Vuelve pronto o guárdala en tu lista de deseos — te avisaremos cuando alguien la publique.</div>
              </div>
            )}
            <div className="space-y-3">
              {publications.slice(0, visibleCount).map((p) => (
                <div key={p.id} data-testid={`pub-row-${p.id}`} className="bg-[#101010] border border-white/10 rounded-[20px] p-3 sm:p-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Fotos REALES del vendedor: mosaico clickeable → lightbox con todas.
                        Son esenciales: muestran el estado real de la carta y son evidencia
                        en disputas. En móvil: 64×80 (compacto). En sm+: 80×96. */}
                    {p.photos?.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => openLightbox(p.photos.map(ph => ({ path: ph, scope: "public" })), 0)}
                        data-testid={`pub-real-photos-${p.id}`}
                        title={`Ver ${p.photos.length} ${p.photos.length === 1 ? "foto real" : "fotos reales"} del vendedor`}
                        className="relative w-[84px] h-[104px] shrink-0 rounded-[12px] overflow-hidden bg-[#080808] border border-white/10 hover:border-[#FF4438] transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF4438]"
                      >
                        {p.photos.length === 1 ? (
                          <img
                            src={publicImageUrl(p.photos[0], 200)}
                            alt="Foto real del vendedor"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = card.image_url ? cardImageUrl(card.image_url) : "/hit-isotipo.png"; e.currentTarget.style.opacity = card.image_url ? ".35" : ".2"; e.currentTarget.style.objectFit = card.image_url ? "cover" : "contain"; }}
                          />
                        ) : (
                          <div className="grid grid-cols-2 gap-[1px] w-full h-full bg-[#262626]">
                            {p.photos.slice(0, 4).map((ph, idx) => (
                              <div key={idx} className="relative bg-[#0A0A0A] overflow-hidden">
                                <img
                                  src={publicImageUrl(ph, 200)}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                                {idx === 3 && p.photos.length > 4 && (
                                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-white text-xs font-bold">
                                    +{p.photos.length - 4}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="sx-mono absolute bottom-0 inset-x-0 bg-[rgba(8,8,8,.88)] text-[8px] text-white/80 text-center py-1 uppercase tracking-[.1em]">
                          {p.photos.length} {p.photos.length === 1 ? "foto real" : "fotos reales"}
                        </div>
                      </button>
                    ) : (
                      <div className="relative w-[84px] h-[104px] shrink-0 bg-[#141414] rounded-[12px] border border-white/10 overflow-hidden grid place-items-center" data-testid={`pub-no-photo-${p.id}`}>
                        {card.image_url
                          ? <img src={cardImageUrl(card.image_url)} alt="" className="w-full h-full object-cover opacity-[.35]" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/hit-isotipo.png"; e.currentTarget.className = "w-10 h-10 object-contain opacity-20"; }} />
                          : <img src="/hit-isotipo.png" alt="" className="w-10 h-10 object-contain opacity-20" />}
                        <div className="sx-mono absolute bottom-0 inset-x-0 bg-[rgba(8,8,8,.88)] text-[8px] text-white/60 text-center py-1 uppercase tracking-[.1em]">Sin fotos</div>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                        <span className="flex flex-col items-center gap-1 shrink-0"><HitAvatar avatarId={p.seller?.avatar_id} name={p.seller?.name} size={56} lite rank={frameOf(p.seller)} testid={`pub-seller-avatar-${p.id}`} /><RankName rank={p.seller?.rank || "common"} className="!text-[8px]" /></span>
                        {p.seller?.id ? (
                          <Link to={`/vendedor/${p.seller.id}`} data-testid={`seller-link-${p.seller.id}`} className="text-sm font-bold hover:text-[#FF4438] hover:underline truncate max-w-[160px] sm:max-w-none">
                            {p.seller.name || "Vendedor"}
                          </Link>
                        ) : (
                          <span className="text-sm font-medium truncate max-w-[160px] sm:max-w-none">{p.seller?.name || "Vendedor"}</span>
                        )}
                        <span className="sx-mono text-[9px] uppercase px-2 py-1 rounded-md border border-white/15 text-white/70" title={condDesc(p.condition)} data-testid={`pub-condition-${p.id}`}>
                          Cond: {p.condition}
                        </span>
                        {p.yugioh_set_code && (
                          <span className="sx-mono text-[9px] uppercase px-2 py-1 rounded-md border border-[#fbbf24] text-[#fbbf24]" data-testid={`pub-printing-${p.id}`}>
                            {p.yugioh_set_code} · {ygoRarityLabel(p)}
                          </span>
                        )}
                        <span className="sx-mono text-[9px] uppercase px-2 py-1 rounded-md border border-white/15 text-white/70">
                          Cant: {p.quantity}
                        </span>
                      </div>
                      <div className="text-[11px] sm:text-xs text-white/50 mt-1.5 break-words">Publicado {new Date(p.created_at).toLocaleDateString("es-EC")} · <MapPin size={10} className="inline mb-0.5" /> {p.seller_city || p.seller?.city || "—"}</div>
                      {p.ship_cost !== undefined && (
                        <div className="text-[11px] sm:text-xs text-white/50 mt-1">
                          Envío: {p.ship_cost === 0 ? "GRATIS" : `$${p.ship_cost.toFixed(2)}`}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="sx-mono text-[8px] uppercase tracking-[.14em] text-white/45">Precio final</div>
                      <div className="text-[24px] font-extrabold tracking-[-.04em] leading-none text-[#FF4438]">${(p.buyer_pays ?? p.price).toFixed(2)}</div>
                    </div>
                        <button
                          type="button"
                          data-testid={`pub-report-${p.id}`}
                          aria-label="Reportar publicación"
                          onClick={() => report("publication", p.id)}
                          className="w-11 h-11 rounded-full border border-white/10 grid place-items-center text-white/50 hover:text-[#FF4438] hover:border-[#FF4438] transition-colors"
                        >
                          <Flag size={15} />
                        </button>
                        <button
                          type="button"
                          data-testid={`pub-buy-${p.id}`}
                          onClick={() => buy(p.id)}
                          disabled={addingId === p.id}
                          className="h-11 px-5 rounded-full bg-[#FF4438] hover:bg-[#ff5a4f] text-[#080808] font-bold text-sm inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                        >
                          <BusyLabel busy={addingId === p.id} text="Agregando…"><ShoppingCart size={15} /> Al carrito</BusyLabel>
                        </button>
                  </div>

                  {/* Fila de avisos — ancho completo abajo. Feb 2026: sacado
                      de la columna central para que en móvil no quede como
                      tira vertical ilegible (cada palabra en su propia línea). */}
                  {((p.free_ship_min_items || p.free_ship_min_amount) || p.graded || p.local_only || p.meetup_available || (!user && p.meetup_offered_by_seller)) && (
                    <div className="mt-3 space-y-1.5">
                      {(p.free_ship_min_items || p.free_ship_min_amount) && (
                        <div className="block bg-emerald-500/10 border border-emerald-500/40 rounded px-2.5 py-1.5 text-emerald-200 text-[11px] sm:text-xs leading-relaxed" data-testid={`free-ship-explainer-${p.id}`}>
                          🚚 <b>Envío GRATIS</b> si {p.free_ship_min_items && <>llevas <b>{p.free_ship_min_items} o más</b> artículos</>}{p.free_ship_min_items && p.free_ship_min_amount && <> <b>o</b> </>}{p.free_ship_min_amount && <>tu pedido suma <b>${p.free_ship_min_amount}</b> o más</>} de este vendedor.
                        </div>
                      )}
                      {p.local_only && p.local_province && (
                        <div className="block bg-amber-500/10 border border-amber-500/40 rounded px-2.5 py-1.5 text-amber-200 text-[11px] sm:text-xs" data-testid={`local-only-badge-${p.id}`}>
                          Solo envíos en <b>{p.local_province}</b>
                        </div>
                      )}
                      {p.first_edition && (
                        <div className="inline-block bg-amber-500/20 border border-amber-500/60 rounded px-2 py-1 text-amber-100 text-[11px] font-bold tracking-wider" data-testid={`first-edition-badge-${p.id}`}>1.ª ED.</div>
                      )}
                      {p.graded && (
                        <div className="inline-block bg-purple-600/30 border border-purple-500/60 rounded px-2 py-1 text-purple-100 text-[11px] font-semibold" data-testid={`graded-badge-${p.id}`}>
                          🏆 {p.graded_house} {p.graded_grade}
                        </div>
                      )}
                      {p.meetup_available && (
                        <div className="block bg-[#151515] border border-white/10 rounded-[12px] px-3 py-2 text-white/75 text-[11px] sm:text-xs leading-relaxed" data-testid={`meetup-hint-${p.id}`}>
                          🤝 Encuentro presencial gratis en <b className="text-white font-bold">{p.seller?.city}</b>. Elige esta opción al pagar.
                        </div>
                      )}
                      {!user && !p.meetup_available && p.meetup_offered_by_seller && p.seller?.city && (
                        <div className="block bg-[#151515] border border-white/10 rounded-[12px] px-3 py-2 text-white/75 text-[11px] sm:text-xs leading-relaxed" data-testid={`meetup-hint-public-${p.id}`}>
                          🤝 Este vendedor acepta encuentros presenciales en <b className="text-white font-bold">{p.seller.city}</b>. Inicia sesión para ver si aplica a ti.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {visibleCount < publications.length && (
              <div className="mt-4 text-center">
                <Button
                  data-testid="pub-load-more"
                  onClick={() => setVisibleCount((n) => Math.min(n + PAGE_SIZE, publications.length))}
                  variant="outline"
                  className="border-[#262626] text-neutral-300 hover:border-[#EF4444] hover:text-[#EF4444]"
                >
                  Ver más publicaciones ({publications.length - visibleCount} restantes)
                </Button>
              </div>
            )}
          </div>
          {fromListing && (
            <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#080808]/[.96] backdrop-blur-[12px] border-t border-white/10 px-4 pt-3 flex items-center gap-3"
              style={{ paddingBottom: "calc(.75rem + env(safe-area-inset-bottom, 0px))" }} data-testid="card-sticky-bar">
              <div className="min-w-0 flex-1">
                <div className="sx-mono text-[8px] uppercase tracking-[.14em] text-white/50">Desde · {publications.length} publicaci{publications.length === 1 ? "ón" : "ones"}</div>
                <div className="text-[24px] font-extrabold tracking-[-.04em] leading-none text-[#FF4438]">${fromListing.buyer_pays.toFixed(2)}</div>
              </div>
              <button type="button" data-testid="card-sticky-see-pubs"
                onClick={() => document.getElementById("card-publications")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="h-[50px] px-6 rounded-full bg-[#FF4438] hover:bg-[#ff5a4f] text-[#080808] font-bold text-sm transition-colors">
                Ver publicaciones
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
